import { FunctionComponent, useRef, useEffect } from "react";
import styles from "../../../Screens/AddProject/AddProject.module.css";
import { useState } from "react";
import Loading from "../../Loading/Loading";
import TextInput from "../../TextInput/InputText";
import { useLocation } from "react-router-dom";
import { editProjectDetails } from "../../../Services/resources";
import { connect, useDispatch } from "react-redux";
import { showSnackbarAction } from "../../../Redux/actions";
import { fetchProjects, fetchpropertyStage, fetchpropertyType } from "../../../Services/contacts";
import { themeColors } from "../../theme";
import { useTheme, useMediaQuery, Box, Grid } from "@mui/material";
import "../../../theme-vars.css";
import { Circles } from "react-loader-spinner";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import DropDownText from "../../DropDownText/DropDownText";

/* -------------------------------------------------------
   Static dropdown list for Project Status
------------------------------------------------------- */
const propertyStatus = [
  { label: "Launched", value: "Launched" },
  { label: "Pre Launch", value: "Pre Launch" },
  { label: "Intermediate Occupation", value: "Intermediate Occupation" }
];

/* -------------------------------------------------------
   Component Props
------------------------------------------------------- */
type props = { history: any; user: any, theme: any, projectHeader: any };

/* -------------------------------------------------------
   EditMasterProjectModal Component
   - Used for editing master project details
   - Loads project metadata (types, stages)
   - Updates project info via editProjectDetails()
------------------------------------------------------- */
const ProjectEditModal: FunctionComponent<props> = ({ history, user, theme, projectHeader }) => {
  /* ---------- Theme & Responsiveness ---------- */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

  /* ---------- Routing & Redux ---------- */
  const location: any = useLocation();
  const dispatcher = useDispatch();

  /* ---------- Local State ---------- */
  const [load, setLoad] = useState(false);

  // Dropdown selections
  const [propertyTypeSelected, setPropertyTypeSelected] = useState<any>({ label: "Select", value: "Select" });
  const [projectStatusSelected, setProjectStatusSelected] = useState<any>({ value: "Select", label: "Select" });

  // Dropdown option lists
  const [projectsList, setProjectsList] = useState<any[] | undefined>(undefined);
  const [propertyTypes, setPropertyTypes] = useState<{ label: string; value: string }[]>([]);
  const [propertyStages, setPropertyStages] = useState<{ label: string; value: string }[]>([]);
  const [propertyStageSelected, setPropertyStageSelected] = useState<any>({ label: "Select", value: "Select" });

  /* ---------- Form Refs ---------- */
  const projectNameRef: any = useRef();
  const developerNameRef: any = useRef();
  const addressRef: any = useRef();
  const reraRef: any = useRef();
  const walkthroughRef: any = useRef();

  /* -------------------------------------------------------
   Fetch Property Stage List
------------------------------------------------------- */
  useEffect(() => {
    const getPropertyState = fetchpropertyStage(
      user.organization_id,
      (data) => {
        // Ensure propertyTypes is an array of { label, value }
        const formatted = Array.isArray(data)
          ? data.map((item: any) =>
            typeof item === "string"
              ? { label: item, value: item }
              : item
          )
          : [];
        setPropertyStages(formatted);
      }
    );
    return () => {
      getPropertyState();
    };
  }, []);

  /* -------------------------------------------------------
    Set initial selected dropdown values
------------------------------------------------------- */
  useEffect(() => {
    setPropertyTypeSelected({
      label: location.state.detail.property_type,
      value: location.state.detail.property_type,
    })
    setProjectStatusSelected({
      label: location.state.detail.project_status,
      value: location.state.detail.project_status,
    })
    setPropertyStageSelected(
      {
        label: location.state.detail.property_stage,
        value: location.state.detail.property_stage,
      }
    )
  }, [propertyTypes, propertyStatus, propertyStages]);

  /* -------------------------------------------------------
   Fetch Property Types + Projects List
------------------------------------------------------- */
  useEffect(() => {
    const unNewPropertyType = fetchpropertyType(
      user.organization_id,
      (data) => {
        // Ensure propertyTypes is an array of { label, value }
        const formatted = Array.isArray(data)
          ? data.map((item: any) =>
            typeof item === "string"
              ? { label: item, value: item }
              : item
          )
          : [];
        setPropertyTypes(formatted);
      }
    );

    if (user.organization_id) {
      const unsub = fetchProjects(
        (data) => setProjectsList(data),
        user.organization_id
      );
      return () => {
        unsub();
        unNewPropertyType();
      };
    }
  }, []);

  /* -------------------------------------------------------
    Validation before submit
    - Fills empty fields with original values
------------------------------------------------------- */
  const handleCheck = () => {
    if (projectNameRef.current.value === "") {
      projectNameRef.current.value = location.state.detail.project_name;
    }
    if (developerNameRef.current.value === "") {
      developerNameRef.current.value = location.state.detail.developer_name;
    }
    if (addressRef.current.value === "") {
      addressRef.current.value = location.state.detail.address;
    }

    if (walkthroughRef.current.value === "") {
      walkthroughRef.current.value = location.state.detail.walkthrough_link;
      dispatcher(showSnackbarAction("Please Enter Walkthrough Link", "error"));
      return false;
    }
    if (propertyTypeSelected.label === "Select") {
      propertyTypeSelected.label = location.state.detail.property_type;
    }
    if (projectStatusSelected.label === "Select") {
      projectStatusSelected.label = location.state.detail.project_status;
    }
    if (propertyStageSelected.label === "Select") {
      propertyStageSelected.label = location.state.detail.property_stage;
    }

    return true;
  };

  /* -------------------------------------------------------
    Submit Handler: Calls editProjectDetails()
------------------------------------------------------- */
  const edit = (e: any) => {
    e.preventDefault();
    const val = handleCheck();
    if (val && projectsList) {
      setLoad(true);

      editProjectDetails(
        user.organization_id,
        projectsList,
        location.state.detail.project_name,
        projectNameRef.current.value,
        developerNameRef.current.value,
        addressRef.current.value,
        propertyTypeSelected.label,
        projectStatusSelected.label,
        propertyStageSelected.label,
        reraRef.current.value,
        walkthroughRef.current.value,
        dispatcher,
        (data) => setLoad(data)
      );
    }
  };

  /* -------------------------------------------------------
   Dropdown Handlers
------------------------------------------------------- */
  const handlePropertyType = (e: any) => {
    const selectedValue = e.target.value;
    const selectedObj = propertyTypes.find((item: any) => item.value === selectedValue) || { label: "Select", value: "Select" };
    setPropertyTypeSelected(selectedObj);
  };

  const handlePropertyStage = (e: any) => {
    const selectedValue = e.target.value;
    const selectedObj = propertyStages.find((item: any) => item.value === selectedValue) || { label: "Select", value: "Select" };
    setPropertyStageSelected(selectedObj);
  };

  const handlePropertyStatus = (e: any) => {
    const selectedValue = e.target.value;
    const selectedObj = propertyStatus.find((item: any) => item.value === selectedValue) || { label: "Select", value: "Select" };
    setProjectStatusSelected(selectedObj);
  }

  /* -------------------------------------------------------
    Theme-based container styles
------------------------------------------------------- */
  const containerStyles = { backgroundColor: theme ? themeColors.backgroundColor : themeColors.backgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }
  const cardContainerStyles = { backgroundColor: theme ? themeColors.cardBackgroundColor : themeColors.cardBackgroundColorDark, color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark }

  /* -------------------------------------------------------
   Render Component
------------------------------------------------------- */
  return (
    <div className={styles.parent} style={{ ...containerStyles, marginTop: isMobile ? "6%" : "4%", fontFamily: "var(--font-family-main)" }}>
      {load === true && <Loading />}

      {/* Mobile Title */}
      {isMobile && (
        <div className="d-flex flex-row w-100 px-4 pt-2">
          <h5 style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-large)" }}>Edit Project Details</h5>
        </div>
      )}

       {/* Responsive Form Wrapper */}
      <div
        className="w-100 d-flex flex-column gap-2 px-4"
        style={{
          padding: isMobile ? '0' : '',
          maxWidth: isMobile ? '100vw' : undefined,
          margin: isMobile ? '0' : '0 auto',
          overflowY: isMobile ? 'auto' : undefined,
          maxHeight: isMobile ? 'calc(100vh - 120px)' : undefined,
        }}
      >
         {/* Form Card */}
        <form
          className={`card shadow rounded mt-2${!isMobile ? ` ${styles.forcePadding}` : ""}`}
          style={{
            ...cardContainerStyles,
            padding: isMobile ? '16px' : '32px',
            boxSizing: 'border-box',
            minHeight: isMobile ? 'auto' : undefined,
            marginBottom: isMobile ? '50px' : 0,
          }}
        >
          <Grid container spacing={isMobile ? 2 : 3}>
             {/* Developer Name */}
            <Grid item xs={12} sm={4}>
              <TextInput
                title="Developer Name"
                style={{ backgroundColor: "#fff", width: '100%' }}
                ref={developerNameRef}
                value={location.state.detail.developer_name}
                required={true}
              />
            </Grid>

            {/* Project Name */}
            <Grid item xs={12} sm={4}>
              <TextInput
                title="Project Name"
                style={{ backgroundColor: "#fff", width: '100%' }}
                ref={projectNameRef}
                value={location.state.detail.project_name}
                required={true}
              />
            </Grid>

             {/* Property Type Dropdown */}
            <Grid item xs={12} sm={4}>
              <DropDownText
                title="Property Type"
                options={propertyTypes}
                value={propertyTypeSelected.value === "Select" ? "" : propertyTypeSelected.value}
                onChange={handlePropertyType}
                required
                labelKey="label"
                valueKey="value"
              />
            </Grid>

             {/* Address */}
            <Grid item xs={12} sm={4}>
              <TextInput
                title="Address"
                style={{ backgroundColor: "#fff", width: '100%' }}
                ref={addressRef}
                value={location.state.detail.address}
              />
            </Grid>

             {/* RERA Link */}
            <Grid item xs={12} sm={4}>
              <TextInput
                title="Rera Link"
                style={{ backgroundColor: "#fff", width: '100%' }}
                ref={reraRef}
                value={location.state.detail.rera_link}
              />
            </Grid>

            {/* Walkthrough Link */}
            <Grid item xs={12} sm={4}>
              <TextInput
                title="Walkthrough Link"
                style={{ backgroundColor: "#fff", width: '100%' }}
                ref={walkthroughRef}
                value={location.state.detail.walkthrough_link}
              />
            </Grid>

            {/* Property Stage */}
            <Grid item xs={12} sm={4}>
              <DropDownText
                title="Property Stage"
                options={propertyStages}
                value={propertyStageSelected.value === "Select" ? "" : propertyStageSelected.value}
                onChange={handlePropertyStage}
                required
                labelKey="label"
                valueKey="value"
              />
            </Grid>

             {/* Property Status */}
            <Grid item xs={12} sm={4}>
              <DropDownText
                title="Property Status"
                options={propertyStatus}
                value={projectStatusSelected.value === "Select" ? "" : projectStatusSelected.value}
                onChange={handlePropertyStatus}
                required
                labelKey="label"
                valueKey="value"
              />
            </Grid>
            {/* Remove submit button from inside form for mobile */}
            {!isMobile && (
              <Grid item xs={12} style={{ marginTop: 12 }}>
                <StyledSubmitButton
                  onClick={edit}
                  themeMode={themeMUI.palette.mode}
                >
                  {load ? (
                    <span style={{ display: "flex", justifyContent: "center" }}>
                      <Circles color="#fff" height={20} width={20} />Submit...
                    </span>
                  ) : (
                    <>Submit</>
                  )}
                </StyledSubmitButton>
              </Grid>
            )}
          </Grid>
        </form>
      </div>
        {/* Mobile Footer Submit Button */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "white",
            p: 2,
            borderTop: "1px solid #ddd",
            boxShadow: "0 -2px 6px rgba(0,0,0,0.1)",
            zIndex: 100,
          }}
        >
          <StyledSubmitButton
            onClick={edit}
            themeMode={themeMUI.palette.mode}
            isMobile={isMobile}
          >
            {load ? (
              <span style={{ display: "flex", justifyContent: "center" }}>
                <Circles color="#fff" height={20} width={20} />Submit...
              </span>
            ) : (
              <>Submit</>
            )}
          </StyledSubmitButton>
        </Box>
      )}
    </div>
  );
};

/* -------------------------------------------------------
   Redux State Mapping
------------------------------------------------------- */
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    theme: state.theme.isLightMode,
    projectHeader: state.projectHeaderList.data,
  };
};

export default connect(mapStateToProps)(ProjectEditModal);
